import React, { ReactNode } from "react";

import Progress from "antd/es/progress";
import { IntlShape } from "react-intl";

import ImportStatus from "@mapmycustomers/shared/enum/imports/ImportStatus";
import Import from "@mapmycustomers/shared/types/imports/Import";

import ButtonLink from "@app/component/ButtonLink";
import ImportHistoryRow from "@app/scene/settings/component/Imports/type/ImportHistoryRow";
import convertToHistoryRow from "@app/scene/settings/component/Imports/util/convertToHistoryRow";
import openChat from "@app/util/openChat";

import ErrorList from "../ErrorList";
import styles from "../FinalizeStep.module.scss";

import doesImportHaveSkipFile from "./doesImportHaveSkipFile";

const getSubtitle = (intl: IntlShape, importRecord?: Import): ReactNode => {
  if (!importRecord || importRecord.status === ImportStatus.QUEUED) {
    return intl.formatMessage({
      id: "imports.finalizeStep.status.queued.subTitle",
      defaultMessage:
        "Others are trying to import right now, but no need to stick around. You can check import status by returning to the main import page.",
      description: "Import status sub title - QUEUED",
    });
  } else if (importRecord.status === ImportStatus.RUNNING) {
    return (
      <div className={styles.statusSubTitleContainer}>
        <Progress percent={importRecord.metadata.progress} />
        <div>
          {intl.formatMessage({
            id: "imports.finalizeStep.status.running.subTitle",
            defaultMessage:
              "No need to stick around. You can check import status by returning to the main import page.",
            description: "Import status sub title - RUNNING",
          })}
        </div>
      </div>
    );
  } else if (importRecord.status === ImportStatus.COMPLETED) {
    const importRecordAsHistoryRow: ImportHistoryRow = convertToHistoryRow(importRecord);
    const haveWarnings = doesImportHaveSkipFile(importRecord);
    return (
      <div className={styles.statusSubTitleContainer}>
        {!haveWarnings && <Progress percent={importRecord.metadata.progress} />}
        <div>
          {intl.formatMessage(
            {
              id: "imports.finalizeStep.status.completed.subTitle",
              defaultMessage:
                "{created} records created, {changed} records changed, {warningCount} warnings, {errorCount} errors.",
              description: "Import status sub title - COMPLETED",
            },
            {
              changed: importRecordAsHistoryRow.metadata.updatedCount,
              created: importRecordAsHistoryRow.metadata.createdCount,
              errorCount: importRecordAsHistoryRow.errorsCount,
              warningCount: importRecordAsHistoryRow.warningsCount,
            }
          )}
          {haveWarnings && !importRecord.skipFileId && (
            <div className={styles.warningDescription}>
              {intl.formatMessage({
                id: "imports.finalizeStep.status.completed.warningDescription.noSkipFile",
                defaultMessage:
                  "We’re currently processing a skip file that exposes errored rows and lets you know how to make appropriate fixes. This may take up to a few minutes and can be accesed from your Import History.",
                description: "COMPETED Import status warning descriptions with no file",
              })}
            </div>
          )}
          {haveWarnings && importRecord.skipFileId && (
            <div className={styles.warningDescription}>
              {intl.formatMessage({
                id: "imports.finalizeStep.status.completed.warningDescription",
                defaultMessage:
                  "Download the skip file below to see errored rows and columns, as well as how to fix them.",
                description: "COMPETED Import status warning descriptions with file",
              })}
            </div>
          )}
        </div>
      </div>
    );
  } else {
    return (
      <div className={styles.statusSubTitleContainer}>
        <p>
          {intl.formatMessage(
            {
              id: "imports.finalizeStep.status.errored.subTitle",
              defaultMessage:
                "There was a problem with this import. Please review the error(s) below and try again. If problems persist, <a>contact our support team.</a>",
              description: "Import status sub title - ERRORED",
            },
            { a: (text) => <ButtonLink onClick={openChat}>{text}</ButtonLink> }
          )}
        </p>
        <ErrorList importRecord={importRecord} />
      </div>
    );
  }
};

export default getSubtitle;
